<template>
  <floating-element
    v-bind="$attrs"
    ref="popupElement"
    :teleport-disabled="teleportTarget == null"
    :visible="visible"
    :class="{ '-sidebar-reserved': reserveSidebar }"
    :transition-name="transitionName"
    :x-offset="xOffset"
    :backdrop-visible="backdrop"
    class="popup"
    tag="dialog"
    open="false"
    :movable="true"
    :x="x"
    :y="y"
    :width="width"
    :height="height"
    :min-width="minWidth"
    :min-height="minHeight"
    drag-handle-class="popup__draghandle"
    :x-alignment="xAlignment"
    :y-alignment="yAlignment"
    :resizable="resizable"
    :resizable-edges="resizableEdges"
    :center-offset-x="reserveSidebar ? 308 : 0"
    @update:visible="onVisibilityChange"
    @after-leave="$emit('hidden')"
    @click:backdrop="onBackdropClick"
  >
    <template #default="{ width: parentWidth, height: parentHeight }">
      <div class="popup__draghandle" :class="headerClass">
        <div v-if="title" class="popup__label">
          {{ title }}
        </div>
        <slot name="header" :close="onCloseClick"></slot>
        <button
          v-if="closeButtonVisible"
          class="popup__close button-icon"
          type="button"
          @click="onCloseClick"
        >
          <lucide-icon name="x" />
        </button>
      </div>

      <slot :close="onCloseClick" :width="parentWidth" :height="parentHeight"></slot>
      <!-- </dialog> -->
     </template>
  </floating-element>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import FloatingElement from "@core/ui/FloatingElement.vue";
import LucideIcon from "./LucideIcon.vue";

defineOptions({
  name: "PopupDialog",
  inheritAttrs: false
});

interface Props {
  /**
   * If set, the popup will be positioned centered in the space
   * next to the sidebar.
   * If not set, the popup will be positioned centered in the screen.
   */
  reserveSidebar?: boolean;
  backdrop?: boolean;
  title?: string | null;
  closeButtonVisible?: boolean;
  width?: number | null;
  height?: number | null;
  minWidth?: number;
  minHeight?: number;
  visible?: boolean;
  headerClass?: string;
  resizable?: boolean;
  resizableEdges?: {
    top?: boolean;
    left?: boolean;
    bottom?: boolean;
    right?: boolean;
  };
  x?: number | null;
  y?: number | null;
  yAlignment?: "top" | "center" | "bottom";
  xAlignment?: "left" | "center" | "right";
  xOffset?: number;
  transitionName?: string;
  teleportTarget?: string | null;
}
const props = withDefaults(defineProps<Props>(), {
  reserveSidebar: false,
  backdrop: true,
  title: null,
  closeButtonVisible: false,
  width: null,
  minWidth: 400,
  minHeight: 100,
  visible: true,
  headerClass: "",
  resizable: false,
  resizableEdges: () => ({
    top: true,
    left: true,
    bottom: true,
    right: true
  }),
  x: null,
  y: null,
  xOffset: 0,
  xAlignment: "center",
  yAlignment: "center",
  transitionName: "transition-popup",
  teleportTarget: "#app"
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "show"): void;
  (e: "hide"): void;
  (e: "hidden"): void;
}>();

const popupElement = ref<InstanceType<typeof FloatingElement>>();
const dialogElement = computed(
  () => popupElement.value?.el as HTMLDialogElement | undefined
);

// METHODS

function onVisibilityChange(value: boolean) {
  if (value === true) {
    emit("show");
    if (dialogElement.value) {
      dialogElement.value.show();
    }
  } else {
    emit("hide");
  }
}

function onBackdropClick(event: MouseEvent) {
  if (props.closeButtonVisible) {
    onCloseClick(event);
  }
}

function onCloseClick(mouseEvent?: MouseEvent) {
  mouseEvent?.preventDefault?.();
  emit("close");
}
</script>

<style lang="scss">
.popup {
  margin: 0;
  background-color: var(--color-background-deep);
  pointer-events: all;
  box-shadow: 0 0.1rem 0.5rem var(--color-shadow-2);
  max-height: 100vh;
  max-width: 100%;
  backface-visibility: hidden;
  overflow: auto;
  width: 38rem;
  width: 700px;
  border: none;
  padding: 0;
  border-radius: 0.5rem;

  @supports (-moz-appearance: none) {
    // only firefox supports this fully
    // and it interferes with colors in chrome
    scrollbar-width: thin;
  }

  &.-sidebar-reserved {
    max-width: calc(100% - var(--sidebar-width));
  }

  &__draghandle {
    background-color: transparent;
    overflow: hidden; // used to trap margins.
  }

  &__label {
    position: relative;
    margin: 0 auto;
    width: max-content;
    padding: 0.5em 1em;
    font-weight: bold;
    background-color: var(--color-background-medium);
    flex-grow: 0;
    flex-shrink: 0;
    pointer-events: none;
    text-align: center;
  }

  &__section {
    border-top: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    padding: 1rem;
    &:first-child {
      border-top: 1px solid var(--color-border);
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
    flex-wrap: wrap;
  }

  &__close {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    font-size: 24px;
  }

  &-rounded {
    border-radius: 0.5rem;
  }
}
</style>
